import { MatchingProfilePreviewCard } from "@/components/mentor-profile/matching-profile-preview-card";
import { useUploadMentorAudio } from "@/lib/api/mentor-profile";
import { useAudioPlayback } from "@/lib/hooks/use-audio-playback";
import { useAudioRecorder } from "@/lib/hooks/use-audio-recorder";
import { useAudioTranscoder } from "@/lib/hooks/use-audio-transcoder";
import {
  formatAudioDuration,
  useLiveAudioWaveform,
} from "@/lib/hooks/use-audio-waveform";
import { useSignedMedia } from "@/lib/hooks/use-signed-media";
import {
  setLastAudioIntroMethod,
  type AudioIntroMethod,
} from "@/lib/mentor-profile/analytics";
import { Button } from "@somethings-ui/web";
import {
  Check,
  Loader2,
  Mic,
  Pause,
  Play,
  RotateCcw,
  Sparkles,
  Square,
  X,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { WizardStepLayout } from "./wizard-step-layout";
import type { WizardStepProps } from "./types";

const MAX_DURATION_MS = 40_000;

const TIPS = [
  "Who you are — your name and a quick line about you",
  "A couple of things you're into",
  "Why you became a mentor — the real reason",
  "End with a warm, no-pressure hello",
];

const EXAMPLE_CLIPS = [
  {
    label: "Example Intro 1",
    src: "/assets/mentor-onboarding/audio-examples/example-warm-hello.mp3",
    durationMs: 29_900,
  },
  {
    label: "Example Intro 2",
    src: "/assets/mentor-onboarding/audio-examples/example-why-mentor.mp3",
    durationMs: 39_100,
  },
  {
    label: "Example Intro 3",
    src: "/assets/mentor-onboarding/audio-examples/example-into.mp3",
    durationMs: 33_800,
  },
] as const;

function isMp3Mime(mimeType: string): boolean {
  const type = mimeType.toLowerCase();
  return type.includes("mpeg") || type.includes("mp3");
}

function AudioPlayerBar({
  url,
  fallbackDurationMs,
}: {
  url: string;
  fallbackDurationMs?: number;
}) {
  const { isPlaying, progressMs, durationMs, toggle } = useAudioPlayback(url);
  const totalMs =
    durationMs && Number.isFinite(durationMs) ? durationMs : fallbackDurationMs ?? 0;
  const ratio = totalMs > 0 ? Math.min(1, progressMs / totalMs) : 0;

  return (
    <div className="flex items-center gap-3">
      <button
        type="button"
        onClick={toggle}
        aria-label={isPlaying ? "Pause" : "Play"}
        className="flex size-9 shrink-0 items-center justify-center rounded-full bg-[#1f1e1d] text-white"
      >
        {isPlaying ? (
          <Pause className="size-4" />
        ) : (
          <Play className="ml-0.5 size-4" />
        )}
      </button>
      <span className="shrink-0 font-body text-xs tabular-nums text-[#797774]">
        {formatAudioDuration(progressMs)} / {formatAudioDuration(totalMs)}
      </span>
      <div className="h-1 flex-1 overflow-hidden rounded-full bg-[#e7e0d5]">
        <div
          className="h-full rounded-full bg-[#1f1e1d]"
          style={{ width: `${ratio * 100}%` }}
        />
      </div>
    </div>
  );
}

export function AudioStep({
  draft,
  saveState,
  setField,
  embedded,
  reviewerNotes,
  onContinue,
  onBack,
}: WizardStepProps) {
  const recorder = useAudioRecorder({ maxDurationMs: MAX_DURATION_MS });
  const upload = useUploadMentorAudio();
  const transcoder = useAudioTranscoder();
  const liveBars = useLiveAudioWaveform(recorder.stream);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isPreparing, setIsPreparing] = useState(false);

  const result = recorder.result;
  const reviewUrl = useMemo(
    () => (result ? URL.createObjectURL(result.blob) : null),
    [result],
  );
  useEffect(() => {
    return () => {
      if (reviewUrl) URL.revokeObjectURL(reviewUrl);
    };
  }, [reviewUrl]);

  const savedUrl = draft.profile_audio_url;
  const { url: signedSavedUrl } = useSignedMedia(savedUrl);
  const isRecording =
    recorder.state === "recording" || recorder.state === "starting";
  const isBusy = isPreparing || transcoder.transcoding || upload.isPending;

  const commit = async (
    blob: Blob,
    mimeType: string,
    method: AudioIntroMethod,
  ) => {
    setUploadError(null);
    setIsPreparing(true);
    try {
      const uploadBlob = isMp3Mime(mimeType)
        ? blob
        : await transcoder.transcodeToMp3(blob);

      await new Promise<void>((resolve, reject) => {
        upload.mutate(
          { blob: uploadBlob, filename: "intro.mp3" },
          {
            onSuccess: (url) => {
              setField("profile_audio_url", url);
              setLastAudioIntroMethod(method);
              recorder.reset();
              resolve();
            },
            onError: () => reject(new Error("upload failed")),
          },
        );
      });
    } catch {
      setUploadError("We couldn't save that recording. Please try again.");
    } finally {
      setIsPreparing(false);
    }
  };

  const handleFile = (file: File | undefined) => {
    if (!file) return;
    if (!file.type.startsWith("audio/")) {
      setUploadError("That file isn't audio. Please choose an audio file.");
      return;
    }
    void commit(file, file.type || "audio/mpeg", "uploaded");
  };

  const recorderError = recorder.error
    ? recorder.error.kind === "permission_denied"
      ? "Microphone access was blocked. Enable it in your browser to record."
      : recorder.error.kind === "no_microphone"
        ? "No microphone found. Plug one in or upload a file instead."
        : recorder.error.kind === "unsupported"
          ? "Recording isn't supported in this browser. Try uploading a file."
          : "Something went wrong while recording. Please try again."
    : null;

  const renderCard = () => {
    if (isRecording) {
      return (
        <div className="flex flex-col items-center gap-4 rounded-[21.6px] border border-[#e7e0d5] bg-white px-6 py-8">
          <div className="relative flex size-24 items-center justify-center">
            <span className="absolute inset-0 animate-pulse rounded-full bg-brand-tangelo/15" />
            <span className="relative flex size-16 items-center justify-center rounded-full bg-brand-tangelo/20 text-brand-tangelo">
              <Mic className="size-7" />
            </span>
          </div>
          <div className="flex h-6 items-end gap-[2px]">
            {liveBars.map((v, i) => (
              <span
                key={i}
                className="w-[3px] rounded-full bg-brand-tangelo/70"
                style={{ height: `${Math.max(3, v * 24)}px` }}
              />
            ))}
          </div>
          <div className="flex items-center gap-1.5 font-body text-sm text-[#797774]">
            <span className="size-2 rounded-full bg-brand-tangelo" />
            <span className="tabular-nums text-[#1f1e1d]">
              {formatAudioDuration(recorder.durationMs)}
            </span>
            <span className="tabular-nums">
              / {formatAudioDuration(MAX_DURATION_MS)}
            </span>
          </div>
          <div className="flex items-center gap-4">
            <Button
              size="sm"
              variant="quaternary"
              fullWidth={false}
              leadingIcon={<X className="size-4" />}
              onPress={recorder.reset}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              variant="elevated"
              fullWidth={false}
              leadingIcon={<Square className="size-3.5 fill-current" />}
              onPress={recorder.stop}
            >
              Stop
            </Button>
          </div>
        </div>
      );
    }

    if (reviewUrl && result) {
      return (
        <div className="flex flex-col gap-4 rounded-[21.6px] border border-[#e7e0d5] bg-white px-6 py-6">
          <AudioPlayerBar url={reviewUrl} fallbackDurationMs={result.durationMs} />
          <div className="flex items-center justify-center gap-3">
            <Button
              size="sm"
              variant="quaternary"
              fullWidth={false}
              disabled={isBusy}
              leadingIcon={<RotateCcw className="size-4" />}
              onPress={recorder.reset}
            >
              Re-take
            </Button>
            <Button
              size="sm"
              fullWidth={false}
              disabled={isBusy}
              loading={isBusy}
              leadingIcon={isBusy ? undefined : <Check className="size-4" />}
              onPress={() => void commit(result.blob, result.mimeType, "recorded")}
            >
              {transcoder.transcoding || isPreparing
                ? "Converting…"
                : upload.isPending
                  ? "Saving…"
                  : "Use this"}
            </Button>
          </div>
        </div>
      );
    }

    if (savedUrl) {
      return (
        <div className="flex flex-col gap-4 rounded-[21.6px] border border-[#e7e0d5] bg-white px-6 py-6">
          {signedSavedUrl ? (
            <AudioPlayerBar url={signedSavedUrl} />
          ) : (
            <div className="flex h-12 items-center justify-center text-[#797774]">
              <Loader2 className="size-5 animate-spin" />
            </div>
          )}
          <div className="flex items-center justify-center gap-3">
            <Button
              size="sm"
              variant="quaternary"
              fullWidth={false}
              leadingIcon={<RotateCcw className="size-4" />}
              onPress={() => void recorder.start()}
            >
              Re-record
            </Button>
            <Button
              size="sm"
              variant="quaternary"
              fullWidth={false}
              leadingIcon={<X className="size-4" />}
              onPress={() => setField("profile_audio_url", "")}
            >
              Remove
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div className="flex flex-col items-center gap-4 rounded-2xl border border-dashed border-surfaces-z5 bg-surfaces-z0 px-6 py-8">
        <div className="text-center">
          <p className="font-body text-sm font-medium text-text-primary">
            Record a quick hello
          </p>
          <p className="mt-1 font-body text-xs text-text-secondary">
            A short audio intro helps teens get a feel for you. 30–40 seconds is
            plenty.
          </p>
        </div>
        <Button
          size="sm"
          variant="secondary"
          fullWidth={false}
          disabled={isBusy}
          loading={isBusy}
          leadingIcon={isBusy ? undefined : <Mic className="size-4" />}
          onPress={() => void recorder.start()}
        >
          Record audio
        </Button>
        <Button
          size="sm"
          variant="quaternary"
          fullWidth={false}
          disabled={isBusy}
          onPress={() => fileInputRef.current?.click()}
        >
          or upload a file
        </Button>
      </div>
    );
  };

  return (
    <WizardStepLayout
      reviewerNotes={reviewerNotes}
      embedded={embedded}
      stepIndex={6}
      icon={
        <img
          src="/assets/mentor-onboarding/step-audio-icon.png"
          alt=""
          className="size-10 object-contain"
        />
      }
      heading="Add a quick intro"
      subheading="Hearing a real voice lands differently than text. Teens love it."
      saveState={saveState}
      onBack={onBack}
      onContinue={onContinue}
      continueDisabled={!savedUrl?.trim() || isBusy}
      preview={
        <MatchingProfilePreviewCard
          draft={draft}
          focus={{ region: "media", zoom: 1.05 }}
        />
      }
    >
      <input
        ref={fileInputRef}
        type="file"
        accept="audio/*"
        className="hidden"
        onChange={(e) => {
          handleFile(e.target.files?.[0]);
          e.target.value = "";
        }}
      />

      {renderCard()}

      {(uploadError || recorderError) && (
        <span className="font-body text-sm text-states-error">
          {uploadError ?? recorderError}
        </span>
      )}

      <div className="mt-3 rounded-[16.8px] bg-[#f3eee6] px-3.5 py-3">
        <div className="flex items-center gap-1.5">
          <Sparkles className="size-3.5 text-brand-tangelo" />
          <p className="font-body text-xs font-medium text-[#1f1e1d]/70">
            No script needed — a loose flow if you want one
          </p>
        </div>
        <ol className="mt-1.5 space-y-1">
          {TIPS.map((tip, i) => (
            <li key={tip} className="flex gap-2.5">
              <span className="font-body text-xs font-semibold text-[#1f1e1d]/70">
                {i + 1}
              </span>
              <span className="font-body text-[13px] text-[#1f1e1d]/80">
                {tip}
              </span>
            </li>
          ))}
        </ol>
        <p className="mt-1.5 font-body text-xs text-[#1f1e1d]/70">
          Keep it warm and real. Under 40 seconds is plenty.
        </p>
      </div>

      <div className="mt-3 rounded-[16.8px] border border-[#e7e0d5] bg-white px-3.5 py-3">
        <p className="font-body text-xs font-medium text-[#1f1e1d]">
          Hear a couple of examples
        </p>
        <div className="mt-2.5 flex flex-col gap-2.5">
          {EXAMPLE_CLIPS.map((clip) => (
            <div key={clip.src} className="rounded-[12px] bg-[#faf6ef] px-3 py-2.5">
              <p className="mb-1.5 font-body text-xs text-[#797774]">{clip.label}</p>
              <AudioPlayerBar url={clip.src} fallbackDurationMs={clip.durationMs} />
            </div>
          ))}
        </div>
      </div>
    </WizardStepLayout>
  );
}
